/**
 * Chat history service for AskAsper
 * Fetches conversation sessions and thread messages.
 */

import { ASK_ASPER_BASE_URL } from '../config';

/** Thread shape expected by AgenticTwoPanelLayout sidebar */
export interface SidebarThread {
    id: number;
    thread_id: string;
    thread_name: string;
    user_id: string;
    created_on: string;
    is_pinned: boolean | null;
    pin_no: number | null;
}

/** Raw chat session from GET /chat-sessions API */
export interface ChatSessionApiResponse {
    id?: number | string;
    thread_id?: string;
    thread_name?: string;
    user_id?: string;
    created_on?: string;
    created_at?: string;
    is_pinned?: boolean | null;
    pin_no?: number | null;
    [key: string]: unknown;
}

/** Raw message from chat history API */
export interface ChatHistoryMessageApi {
    id?: number | string;
    thread_id?: string;
    run_id?: string;
    type?: string;
    content?: string;
    user_input?: string;
    question?: string;
    created_on?: string;
    thumbs_up?: boolean | null;
    subjective_answer?: string | null;
    tool_calls?: unknown[];
    additional_kwargs?: {
        intermediate_steps?: Array<{
            step: string;
            status: string;
            content: string | unknown;
            timestamp: number;
        }>;
        charts?: Array<{ viz_data: unknown }>;
        followup_questions?: string;
    };
    answer?: {
        data?: {
            output?: string;
            intermediate_steps?: unknown[];
            visualizations?: Array<{ viz_data: unknown; task_id?: string; task_name?: string }>;
            followup_question?: unknown;
        };
    };
    charts?: Array<{ viz_data: unknown; task_id?: string; task_name?: string }>;
}

function getAuthHeader(): string {
    const token = localStorage.getItem('accessToken');
    return `Bearer ${token}`;
}

/**
 * Fetch chat sessions (conversation threads) from the API.
 */
export async function fetchChatSessions(): Promise<ChatSessionApiResponse[]> {
    const response = await fetch(`${ASK_ASPER_BASE_URL}chat-sessions`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: getAuthHeader(),
        },
    });

    if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    const contentType = response.headers.get('Content-Type');
    if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        const extracted = Array.isArray(data) ? data : (data?.sessions ?? data?.data ?? data?.chat_sessions ?? []);
        return Array.isArray(extracted) ? extracted : [];
    }

    return [];
}

/**
 * Map API chat sessions to thread format expected by AgenticTwoPanelLayout sidebar.
 */
export function mapChatSessionsToThreads(sessions: ChatSessionApiResponse[] | null): SidebarThread[] {
    if (!sessions || !Array.isArray(sessions)) {
        return [];
    }

    return sessions.map((s, index) => ({
        id: typeof s.id === 'number' ? s.id : index,
        thread_id: s.thread_id ?? '',
        thread_name: s.thread_name ?? `Thread ${index + 1}`,
        user_id: s.user_id ?? '',
        created_on: s.created_on ?? (s as { created_at?: string }).created_at ?? '',
        is_pinned: s.is_pinned ?? null,
        pin_no: s.pin_no ?? null,
    }));
}


export interface RenameThreadRequest {
    thread_id: string;
    new_thread_name: string;
}

/**
 * Rename a thread
 */
export async function renameThread(request: RenameThreadRequest): Promise<void> {
    const response = await fetch(`${ASK_ASPER_BASE_URL}chat-session/rename`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: getAuthHeader(),
        },
        body: JSON.stringify(request),
    });

    if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
    }
}

/**
 * Delete a thread and its messages
 */
export async function deleteThread(threadId: string): Promise<void> {
    const response = await fetch(`${ASK_ASPER_BASE_URL}chat-session/delete/${threadId}`, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: getAuthHeader(),
        },
    });

    if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
    }
}


/**
 * Fetch chat history (messages) for a specific thread.
 * Endpoint: GET /chat-sessions/{threadId}/messages
 */
export async function getChatHistory(threadId: string): Promise<ChatHistoryMessageApi[]> {
    const response = await fetch(`${ASK_ASPER_BASE_URL}chat-history/${threadId}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: getAuthHeader(),
        },
    });

    if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    const contentType = response.headers.get('Content-Type');
    if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        return Array.isArray(data) ? data : (data?.messages ?? data?.data ?? []);
    }

    return [];
}
